import { useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Check, Copy, Smartphone } from "lucide-react";
import { useStore } from "../../stores/rootStore";
import { assetUrl } from "../../lib/assets";
import { Modal } from "../ui/Modal";
import { MobileQrCode, mobilePlayUrl } from "./MobileQrCode";

export const PlayOnMobileModal = observer(() => {
	const { ui } = useStore();
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);
	const url = mobilePlayUrl();

	async function copyLink() {
		try {
			await copyText(url);
			setCopied(true);
			ui.toast(t("mobilePlay.linkCopied"), "success");
			window.setTimeout(() => setCopied(false), 1800);
		} catch {
			ui.toast(t("mobilePlay.copyFailed"), "error");
		}
	}

	return (
		<Modal
			open={ui.mobilePlayModalOpen}
			onClose={ui.closeMobilePlay}
			title={t("mobilePlay.title")}
		>
			<div className="flex flex-col items-center text-center">
				<div className="mb-4 grid size-10 place-items-center rounded-full bg-brand/15 text-brand">
					<Smartphone size={20} />
				</div>
				<MobileQrCode
					url={url}
					size={220}
					alt={t("mobilePlay.qrAlt")}
					centerLogoSrc={assetUrl("icon-192.png")}
					className="rounded-lg"
				/>
				<p className="mt-4 max-w-xs text-sm leading-relaxed text-muted">
					{t("mobilePlay.description")}
				</p>

				<div className="mt-5 flex w-full items-center gap-2 rounded-lg border border-line bg-surface-2 p-2 pl-3 text-left">
					<span className="min-w-0 flex-1 truncate text-xs text-muted">{url}</span>
					<button
						type="button"
						onClick={() => void copyLink()}
						className="grid size-9 shrink-0 place-items-center rounded-md bg-white text-black transition hover:bg-white/90"
						aria-label={t("mobilePlay.copyLink")}
						title={t("mobilePlay.copyLink")}
					>
						{copied ? <Check size={16} /> : <Copy size={16} />}
					</button>
				</div>
			</div>
		</Modal>
	);
});

async function copyText(value: string) {
	const textarea = document.createElement("textarea");
	textarea.value = value;
	textarea.style.position = "fixed";
	textarea.style.opacity = "0";
	document.body.appendChild(textarea);
	textarea.select();
	const copied = document.execCommand("copy");
	textarea.remove();
	if (copied) return;

	if (navigator.clipboard?.writeText) {
		await navigator.clipboard.writeText(value);
		return;
	}
	throw new Error("Copy failed");
}
